import { ExtraProduct, FullProduct, LocalStorageProduct } from "ts/api/product";

// A quick order may hold the same product on several lines (e.g. once without
// an extra and once with it), so a stored line is matched to the cart line with
// the same id, option and extras that has not been taken by an earlier line.
const findCartProductIndex = (
  cartProducts: FullProduct[],
  storageProduct: LocalStorageProduct,
  usedIndexes: Set<number>
): number => {
  const extraIds = (storageProduct.extras ?? []).map(({ id }) => id);
  let firstUnusedIndex = -1;

  for (let index = 0; index < cartProducts.length; index += 1) {
    const cartProduct = cartProducts[index];
    if (usedIndexes.has(index) || cartProduct.id !== storageProduct.id) {
      continue;
    }
    if (firstUnusedIndex === -1) {
      firstUnusedIndex = index;
    }
    const cartExtraIds = (cartProduct.extras ?? []).map(({ id }) => id);
    const isSameOption =
      !storageProduct.cpoConfigId ||
      cartProduct.cpoConfigId === storageProduct.cpoConfigId;
    if (
      isSameOption &&
      extraIds.length === cartExtraIds.length &&
      extraIds.every((id) => cartExtraIds.includes(id))
    ) {
      return index;
    }
  }

  if (firstUnusedIndex !== -1) {
    return firstUnusedIndex;
  }
  return cartProducts.findIndex(({ id }) => id === storageProduct.id);
};

const mapStorageProductsToCartProducts = (
  storageProducts: LocalStorageProduct[] | undefined,
  cartProducts: FullProduct[]
): FullProduct[] => {
  const usedIndexes = new Set<number>();

  return (storageProducts ?? []).reduce<FullProduct[]>(
    (acc, storageProduct) => {
      const index = findCartProductIndex(
        cartProducts,
        storageProduct,
        usedIndexes
      );
      if (index === -1) {
        return acc;
      }
      usedIndexes.add(index);
      const showProduct = cartProducts[index];

      const extraIds = storageProduct?.extras?.length
        ? storageProduct?.extras.map(({ id }) => id)
        : [];
      const extras = showProduct?.extras
        ?.filter(({ id }) => extraIds.includes(id))
        .map((extra) => {
          const storageExtra = storageProduct?.extras?.find(
            (extraProduct) => extraProduct.id === extra.id
          );

          return {
            ...extra,
            quantity: storageExtra?.quantity,
          };
        }) as ExtraProduct[];
      const { cpoConfigId, imageId } = storageProduct;

      const productItem = {
        ...showProduct,
        extras,
        cartQuantity: storageProduct?.cartQuantity,
        notes: storageProduct?.notes,
        ...(cpoConfigId && { cpoConfigId }),
        ...(imageId && { imageId }),
        ...(showProduct?.invoiceId && {
          invoiceId: showProduct.invoiceId,
        }),
      };

      return [...acc, productItem];
    },
    []
  );
};

export default mapStorageProductsToCartProducts;
